'use client';

import { Box, Button, Divider, Flex, Group, Text, Paper, Stack, Alert } from '@mantine/core';
import { useFileValidation } from '@/app/assets/components/upload/hooks/useFileValidation';
import { useUploadCredits } from '@/app/assets/components/upload/hooks/useUploadCredits';
import { AssetsUploadModal } from '@/app/assets/components/upload/molecules/AssetsUploadModal';
import AssetsTable from '@/app/assets/components/table/AssetsTable';
import { RawModel } from '@/types/rawModel';
import RawAssetUploader from '@/app/assets/components/upload/molecules/RawAssetUploader';
import styles from '@/app/assets/components/upload/atoms/styles.module.css';
import { UploadProgress } from '@/app/assets/components/upload/atoms/UploadProgress';
import { AssetGroupActions } from '@/app/assets/components/table/molecules/AssetGroupActions';
import { useCallback, useMemo, DragEvent, useRef } from 'react';
import { useAssetGroupActions } from '@/app/assets/components/table/hooks/useAssetGroupActions';
import { DownloadProgress } from '@/app/assets/components/table/atoms/DownloadProgress';
import { useSearchParams } from 'next/navigation';
import { useUploadAssets } from '@/app/assets/contexts/UploadAssetsContext';
import { useOptimisticAssets } from '@/app/assets/components/table/hooks/useOptimisticAssets';
import { useUploadPendingAssets } from '@/app/assets/components/upload/hooks/useUploadPendingAssets';
import { useDisclosure } from '@mantine/hooks';
import { ImportSettingsModal } from '@/app/assets/components/upload/organisms/ImportSettingModal';
import { ModelImportConfig, useImportOptions } from '@/app/assets/components/upload/hooks/useImportOptions';
import { useAllAssets, useAssetsQuery } from '../hooks/useAssetsQuery';
import { useAssetsUploadContext } from '@/app/assets/components/upload/contexts/useAssetsUploadContext';
import { AssetRowDescriptor } from '@/app/assets/components/table/hooks/useAssetsTable';
import { useRowSelection } from '../contexts/RowSelectionContext';
import { ShowOnlyOutputsToggle } from '@/app/assets/components/atoms/ShowOnlyOutputsToggle';
import { useGrowthbookContext } from '@/app/contexts/GrowthbookContext';
import { SelectionCounter } from './atoms/SelectionCounter';
import { TablePagination } from '@/app/components/shared/table/TablePagination';
import { useAssetPagination } from './table/hooks/useAssetPagination';
import { AssetsFallbackSkeleton } from '@/app/assets/components/table/Skeletons';
import { FileDroppableStack } from '@/app/components/shared/FileDroppableStack';
import { useUserQuery } from '@/shared/hooks/useUserQuery';

type AssetsUploadProps = {
  userMaxAllowedFileSize: number | null;
  hasCadSupport: boolean;
  hasAdvancedCadSupport: boolean;
  hasRapidPBRSupport: boolean;
  storageLeft: number;
  storageLimit: number;
  showOnlyOutputs: boolean;
};

export default function AssetsPage({
  userMaxAllowedFileSize,
  hasCadSupport,
  hasAdvancedCadSupport,
  hasRapidPBRSupport,
  storageLeft,
  storageLimit,
  showOnlyOutputs,
}: AssetsUploadProps) {
  const { data: rawModels, isLoading, isFetching, isError } = useAssetsQuery();

  const assets = rawModels?.data ?? [];
  const allCachedAssets = useAllAssets();
  const allAssets = allCachedAssets.length < assets.length ? assets : allCachedAssets;

  const { opened, close: closeUploadAssets, open: openUploadAssets } = useUploadAssets();
  const searchParams = useSearchParams();
  const { dispatchAssetAction } = useOptimisticAssets();
  const [isUploadConfigModalOpen, { toggle: toggleUploadConfigModal, close: closeUploadConfigModal }] = useDisclosure();

  const {
    pbrOptions,
    pbrOptionsForm,
    CADOptions,
    CADOptionsForm,
    transformModelImportConfig,
    isError: pbrOptionsError,
    isLoading: pbrOptionsLoading,
  } = useImportOptions(isUploadConfigModalOpen);
  const onFilesBeingUploaded = useCallback(
    (rawAssets: RawModel[]) => {
      dispatchAssetAction({ action: 'add', payload: rawAssets });
    },
    [dispatchAssetAction]
  );
  const onFilesUploaded = useCallback(
    (rawAssets: RawModel[]) => {
      dispatchAssetAction({ action: 'update', payload: rawAssets });
    },
    [dispatchAssetAction]
  );

  const { files, isUploadPending, removeFile, removeAllFiles, uploadFiles, uploadProgress, onFileDropped } =
    useAssetsUploadContext(onFilesBeingUploaded, onFilesUploaded);

  const { data: user } = useUserQuery();
  const planImportCost = user?.plan.details.import_cost;

  const { fileValidationErrors, pbrFiles, isValidating, CADFiles } = useFileValidation(
    userMaxAllowedFileSize,
    hasCadSupport,
    hasAdvancedCadSupport,
    hasRapidPBRSupport,
    storageLeft,
    files,
    planImportCost
  );

  const shouldConfigureAdditionalCADOptions = CADFiles.length > 0 && !!user?.plan.details.advanced_cad_support;

  const onUploadFiles = () => {
    if (pbrFiles.length || shouldConfigureAdditionalCADOptions) {
      toggleUploadConfigModal();
      return false;
    }

    uploadFiles();
    return true;
  };

  useUploadPendingAssets(rawModels?.data ?? [], ({ id, state }) =>
    dispatchAssetAction({
      action: 'update',
      payload: [{ id, upload_status: state }],
    })
  );
  const { credits } = useUploadCredits(files);

  const noAssetsAndNoSearchParam = useMemo(
    () => rawModels?.data.length === 0 && !isUploadPending && !searchParams.get('query'),
    [isUploadPending, searchParams, rawModels]
  );

  const { rowSelection, setRowSelection } = useRowSelection();

  const isRawAssetSelected = useMemo(
    () =>
      Object.keys(rowSelection).some((v) => {
        const selection = JSON.parse(v) as AssetRowDescriptor;
        const asset = allAssets.find((i) => i.id === selection.id && i.assetType === selection.type);
        return asset?.assetType === 'raw' && asset.upload_status === 'complete';
      }),
    [allAssets, rowSelection]
  );

  const isUnprocessableSelected = useMemo(
    () =>
      Object.keys(rowSelection).some((v) => {
        const selection = JSON.parse(v) as AssetRowDescriptor;
        const asset = allAssets.find((i) => i.id === selection.id && i.assetType === selection.type);
        return asset?.assetType !== 'raw' || asset?.upload_status !== 'complete';
      }),
    [allAssets, rowSelection]
  );

  const {
    pendingOutputs,
    addOnJobEndCallback,
    onDeleteAssets,
    onDownloadAssets,
    onRunWorkflow,
    downloadProgress,
    isDownloadPending,
  } = useAssetGroupActions(allAssets, dispatchAssetAction, isRawAssetSelected);

  const uploadFilesHaveErrors = useMemo(
    () =>
      Object.keys(fileValidationErrors.files).length !== 0 ||
      (!!fileValidationErrors.general?.errors && Object.keys(fileValidationErrors.general?.errors).length !== 0),
    [fileValidationErrors.files, fileValidationErrors.general]
  );

  const growthbook = useGrowthbookContext();

  const totalPages = rawModels?.totalPages ?? NaN;
  const { onPageChange, assetsPage } = useAssetPagination(totalPages);

  const handleOnPageAssetDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      const droppedFiles = Array.from(e.dataTransfer.files);

      if (droppedFiles.length) {
        openUploadAssets();
        onFileDropped(droppedFiles);
      }
    },
    [onFileDropped, openUploadAssets]
  );

  if (isLoading) {
    return <AssetsFallbackSkeleton />;
  }

  return (
    <>
      <FileDroppableStack disabled={noAssetsAndNoSearchParam} onDrop={(e) => handleOnPageAssetDrop(e)}>
        <Group justify='space-between' ml='xs'>
          {growthbook?.isOn('show-only-outputs') ? (
            <ShowOnlyOutputsToggle checked={showOnlyOutputs} title='Show only output models' />
          ) : (
            <Box />
          )}
          <AssetGroupActions
            onRun={onRunWorkflow}
            onDelete={onDeleteAssets}
            onDownload={onDownloadAssets}
            isRawAssetSelected={isRawAssetSelected}
            isUnprocessableSelected={isUnprocessableSelected}
          />
        </Group>
        {noAssetsAndNoSearchParam &&
          (totalPages === 1 ? (
            <Paper p='lg' className={`rounded-[20px] ${styles.dashedBorder}`}>
              <Stack>
                <RawAssetUploader
                  storageLimit={storageLimit}
                  storageLeft={storageLeft}
                  credits={credits}
                  onFileDropped={onFileDropped}
                  isUploadPending={isUploadPending}
                  files={files}
                  removeFile={removeFile}
                  removeAllFiles={removeAllFiles}
                  fileValidationErrors={fileValidationErrors}
                />
                <Divider />
                <Flex justify='end'>
                  <Button
                    w='fit-content'
                    disabled={!files.length || isUploadPending || uploadFilesHaveErrors}
                    onClick={onUploadFiles}
                    loading={isValidating}
                  >
                    Upload
                  </Button>
                </Flex>
              </Stack>
            </Paper>
          ) : (
            <AssetsFallbackSkeleton />
          ))}
        {searchParams.get('query') && !isLoading && !isFetching && !assets.length ? (
          <Text>No assets found.</Text>
        ) : null}
        {isUploadPending && <UploadProgress uploadProgress={uploadProgress} />}
        {isError && (
          <Alert color='red'>
            An error ocurred while fetching your assets. Please try again later. If the problem persists, please contact
            the customer service!
          </Alert>
        )}
        {!!assets.length && (
          <>
            <SelectionCounter />
            <AssetsTable
              addOnJobEndCallback={addOnJobEndCallback}
              pendingOutputs={pendingOutputs}
              assets={assets}
              onRowSelectionChange={setRowSelection}
              rowSelection={rowSelection}
              onAssetDelete={dispatchAssetAction}
              useOnlyOutputs={showOnlyOutputs}
              isLoading={isLoading}
              isFetching={isFetching}
            />
            <TablePagination onChange={onPageChange} activePage={assetsPage} totalPages={totalPages} />
          </>
        )}
      </FileDroppableStack>
      <DownloadProgress progress={downloadProgress.progress} isDownloadPending={isDownloadPending} />
      <AssetsUploadModal
        hasErrors={uploadFilesHaveErrors}
        opened={opened}
        onClose={closeUploadAssets}
        storageLimit={storageLimit}
        storageLeft={storageLeft}
        credits={credits}
        onFileDropped={onFileDropped}
        uploadPending={isUploadPending}
        uploadFiles={() => {
          if (onUploadFiles()) {
            closeUploadAssets();
          }
        }}
        files={files}
        removeFile={removeFile}
        removeAllFiles={removeAllFiles}
        fileValidationErrors={fileValidationErrors}
        isValidating={isValidating}
      />
      <ImportSettingsModal
        onSubmit={() => {
          const importConfig = {} as ModelImportConfig;

          if (pbrFiles.length) {
            pbrOptionsForm.onSubmit((values) => {
              importConfig.pbrConfig = transformModelImportConfig(values, pbrOptions ?? []);
            })();
          }

          if (shouldConfigureAdditionalCADOptions) {
            CADOptionsForm.onSubmit((values) => {
              importConfig.CADConfig = { rpdx_config: values };
            })();
          }

          uploadFiles(importConfig);

          closeUploadAssets();
          toggleUploadConfigModal();
        }}
        pbrOptionsForm={pbrOptionsForm}
        CADOptionsForm={CADOptionsForm}
        opened={isUploadConfigModalOpen}
        close={closeUploadConfigModal}
        pbrFiles={pbrFiles}
        pbrOptions={pbrOptions ?? []}
        CADOptions={CADOptions ?? []}
        isError={pbrOptionsError}
        isLoading={pbrOptionsLoading}
        CADFiles={CADFiles}
        shouldConfigureAdditionalCADOptions={shouldConfigureAdditionalCADOptions}
      />
    </>
  );
}
